import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    AppBar,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    IconButton,
    InputBase,
    Paper,
    Box,
    Tabs,
    Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { cloneDeep } from 'lodash';
import TabPanel from '../common/tabpanel';
import { TypeSearchByBusinessId, TypeSearchByTerm } from './bislookupcontainer';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { PAGE_SIZE } from '../common/bislookup';

function SearchBar(props) {
    const [searchTerm, setSearchTerm] = useState(props.searchTerm ? props.searchTerm : '');
    const [searchBusinessId, setSearchBusinessId] = useState(props.searchBusinessId ? props.searchBusinessId : '');
    const { t } = useTranslation();

    const handleSearchChange = (event) => {
        const target = event.target;
        const value = target.value;
        const name = target.name;
        switch (name) {
            case 'searchByTerm':
                setSearchTerm(value);
                break;
            case 'searchByBusinessId':
                setSearchBusinessId(value);
                break;
            default:
                break;
        }
    };

    const doSearch = () => {
        props.search(
            props.tab === 0 ? searchTerm : searchBusinessId,
            props.tab === 0 ? TypeSearchByTerm : TypeSearchByBusinessId
        );
    };

    const handleKeyUp = (e) => {
        if (e.key === 'Enter') {
            doSearch();
        }
    };

    return (
        <Paper sx={{ mt: 1, mb: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
            <InputBase
                sx={{ ml: 1, flex: 1 }}
                name={props.tab === 0 ? 'searchByTerm' : 'searchByBusinessId'}
                placeholder={props.tab === 0 ? t('general.name') : t('general.businessId')}
                onChange={handleSearchChange}
                value={props.tab === 0 ? searchTerm : searchBusinessId}
                onKeyUp={handleKeyUp}
            />
            <IconButton onClick={doSearch}>
                <SearchIcon />
            </IconButton>
        </Paper>
    );
}

export default function BisLookupView(props) {
    const CHECKBOX_TYPE_NAME = 'name';
    const CHECKBOX_TYPE_ID = 'businessId';
    const [searchResult, setSearchResult] = useState(null);
    const [businessIdResult, setBusinessIdResult] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [checkboxValue, setCheckboxValue] = useState({});
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { t } = useTranslation();

    useEffect(() => {
        setSearchResult(props.bisItems);
        setBusinessIdResult(props.businessIdResult);
        setCheckboxValue({});
    }, [props.bisItems, props.businessIdResult]);

    useEffect(() => {
        if (props.searchTerm !== null && props.searchBusinessId === null) {
            setActiveTab(0);
        }
        if (props.searchTerm === null && props.searchBusinessId !== null) {
            setActiveTab(1);
        }
    }, [props.searchBusinessId, props.searchTerm]);

    const onCancel = () => {
        props.cancel();
    };

    const onTabChange = (event, selectedTab) => {
        setActiveTab(selectedTab);
    };

    const checkboxChange = (id, type, value) => {
        let newCheckboxValue = cloneDeep(checkboxValue);
        if (!!!newCheckboxValue[id]) newCheckboxValue[id] = { name: null, businessId: null };
        switch (type) {
            case CHECKBOX_TYPE_NAME:
                newCheckboxValue[id].name = newCheckboxValue[id].name === null ? value : null;
                break;
            case CHECKBOX_TYPE_ID:
                newCheckboxValue[id].businessId = newCheckboxValue[id].businessId === null ? value : null;
                break;
            default:
                break;
        }
        setCheckboxValue(newCheckboxValue);
    };

    const getCheckedState = (id, type) => {
        switch (type) {
            case CHECKBOX_TYPE_NAME:
                return checkboxValue[id] && checkboxValue[id].name ? true : false;
            case CHECKBOX_TYPE_ID:
                return checkboxValue[id] && checkboxValue[id].businessId ? true : false;
            default:
                return false;
        }
    };

    const handleSaveButton = (id) => {
        props.save(checkboxValue[id].name, checkboxValue[id].businessId);
    };

    const formatRegisterationDate = (regdate) => {
        return moment(regdate, 'YYYY-MM-DD').format('DD.MM.YYYY');
    };

    const BisDetailView = (args) => {
        const bisItem = args.bisItem;
        const viewprefix = args.viewprefix ? args.viewprefix : '';
        const { t } = useTranslation();

        if (props.busy) return <CircularProgress color={'secondary'} size={'1rem'} />;
        if (!!!bisItem) return <span>Ei hakutuloksia</span>;
        return (
            <Grid container>
                <Grid item xs={5} sx={{ fontWeight: 'bold' }}>
                    {t('general.name')}
                </Grid>
                <Grid item xs={5}>
                    {bisItem.name}
                </Grid>
                <Grid item xs={2}>
                    <Checkbox
                        checked={getCheckedState(viewprefix + bisItem.businessId, CHECKBOX_TYPE_NAME)}
                        onChange={() =>
                            checkboxChange(viewprefix + bisItem.businessId, CHECKBOX_TYPE_NAME, bisItem.name)
                        }
                    />
                </Grid>

                <Grid item xs={5} sx={{ fontWeight: 'bold' }}>
                    {t('general.businessId')}
                </Grid>
                <Grid item xs={5}>
                    {bisItem.businessId}
                </Grid>
                <Grid item xs={2}>
                    <Checkbox
                        checked={getCheckedState(viewprefix + bisItem.businessId, CHECKBOX_TYPE_ID)}
                        onChange={() =>
                            checkboxChange(viewprefix + bisItem.businessId, CHECKBOX_TYPE_ID, bisItem.businessId)
                        }
                    />
                </Grid>

                <Grid item xs={5} sx={{ fontWeight: 'bold' }}>
                    {t('companySearch.companyForm')}
                </Grid>
                <Grid item xs={5}>
                    {bisItem.companyForm}
                </Grid>
                <Grid item xs={2}></Grid>

                <Grid item xs={5} sx={{ fontWeight: 'bold' }}>
                    {t('companySearch.registrationDate')}
                </Grid>
                <Grid item xs={5}>
                    {formatRegisterationDate(bisItem.registrationDate)}
                </Grid>
                <Grid item xs={2}></Grid>
                <Grid item xs={12} sx={{ textAlign: 'right' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        name="copy"
                        disabled={
                            !getCheckedState(viewprefix + bisItem.businessId, CHECKBOX_TYPE_ID) &&
                            !getCheckedState(viewprefix + bisItem.businessId, CHECKBOX_TYPE_NAME)
                        }
                        onClick={() => handleSaveButton(viewprefix + bisItem.businessId)}>
                        {t('general.copy')}
                    </Button>
                </Grid>
            </Grid>
        );
    };

    return (
        <Dialog open={props.open} fullWidth={true} maxWidth={'md'} fullScreen={fullScreen}>
            <DialogContent>
                <AppBar position="static" color="default" sx={{ mb: 1 }}>
                    <Box display="flex" justifyContent="center" width="100%">
                        <Tabs
                            value={activeTab}
                            onChange={onTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="scrollable"
                            scrollButtons="auto">
                            <Tab value={0} label={t('companySearch.byName')} name="tab-search-by-term" />
                            <Tab value={1} label={t('companySearch.byBusinessId')} name="tab-search-by-businessid" />
                        </Tabs>
                    </Box>
                </AppBar>
                <TabPanel value={activeTab} index={0}>
                    <SearchBar
                        tab={0}
                        searchTerm={props.searchTerm}
                        searchBusinessId={props.searchBusinessId}
                        search={props.search}
                    />
                    {props.busy && <CircularProgress color={'secondary'} size={'1rem'} />}
                    {!props.busy && (searchResult === null || searchResult.totalResults === 0) && (
                        <span>{t('general.noSearchResults')}</span>
                    )}
                    {!props.busy && searchResult !== null && searchResult.totalResults > PAGE_SIZE && (
                        <span>{t('companySearch.tooManyResults')}</span>
                    )}
                    {!props.busy &&
                        searchResult !== null &&
                        searchResult.results.map((result) => (
                            <Accordion key={result.businessId} id={result.businessId}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} name="expand">
                                    <Grid container justifyContent="space-between">
                                        <Grid item>
                                            {result.name} {result.businessId}
                                        </Grid>
                                    </Grid>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <BisDetailView bisItem={result} />
                                </AccordionDetails>
                            </Accordion>
                        ))}
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                    <SearchBar
                        tab={1}
                        searchTerm={props.searchTerm}
                        searchBusinessId={props.searchBusinessId}
                        search={props.search}
                    />
                    <BisDetailView bisItem={businessIdResult} viewprefix={'ids-'} />
                </TabPanel>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    sx={{ bgcolor: 'grey.300', color: 'text.primary', '&:hover': { bgcolor: 'grey.400' } }}
                    name="bis-lookup-cancel"
                    onClick={onCancel}>
                    {t('buttons.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
